/** WebGPU adapter info + device limits (single adapter request per call). */

const ADAPTER_INFO_KEYS = ['vendor', 'architecture', 'device', 'description'];

const LIMIT_KEYS = [
    'maxTextureDimension1D',
    'maxTextureDimension2D',
    'maxTextureDimension3D',
    'maxTextureArrayLayers',
    'maxBindGroups',
    'maxBindGroupsPlusVertexBuffers',
    'maxBindingsPerBindGroup',
    'maxDynamicUniformBuffersPerPipelineLayout',
    'maxDynamicStorageBuffersPerPipelineLayout',
    'maxSampledTexturesPerShaderStage',
    'maxSamplersPerShaderStage',
    'maxStorageBuffersPerShaderStage',
    'maxStorageTexturesPerShaderStage',
    'maxUniformBuffersPerShaderStage',
    'maxUniformBufferBindingSize',
    'maxStorageBufferBindingSize',
    'minUniformBufferOffsetAlignment',
    'minStorageBufferOffsetAlignment',
    'maxVertexBuffers',
    'maxBufferSize',
    'maxVertexAttributes',
    'maxVertexBufferArrayStride',
    'maxInterStageShaderVariables',
    'maxColorAttachments',
    'maxColorAttachmentBytesPerSample',
    'maxComputeWorkgroupStorageSize',
    'maxComputeInvocationsPerWorkgroup',
    'maxComputeWorkgroupSizeX',
    'maxComputeWorkgroupSizeY',
    'maxComputeWorkgroupSizeZ',
    'maxComputeWorkgroupsPerDimension',
    'maxStorageBuffersInFragmentStage',
    'maxStorageTexturesInFragmentStage',
    'maxStorageBuffersInVertexStage',
    'maxStorageTexturesInVertexStage',
];

function emptyAdapterInfo() {
    return { vendor: '', architecture: '', device: '', description: '' };
}

/** Merge non-empty string fields from a GPUAdapterInfo-like object (later sources override). */
function mergeAdapterInfoNonEmpty(target, info) {
    if (!info) {
        return;
    }
    for (let i = 0; i < ADAPTER_INFO_KEYS.length; i++) {
        const k = ADAPTER_INFO_KEYS[i];
        try {
            const s = info[k];
            if (typeof s === 'string' && s !== '') {
                target[k] = s;
            }
        } catch (_) {}
    }
}

function serializeLimits(limits) {
    if (!limits) return null;
    const out = {};
    for (let i = 0; i < LIMIT_KEYS.length; i++) {
        const key = LIMIT_KEYS[i];
        try {
            const v = limits[key];
            if (typeof v === 'number' && Number.isFinite(v)) {
                out[key] = v;
            }
        } catch (_) {}
    }
    return Object.keys(out).length ? out : null;
}

export default async function collectWebGpu() {
    if (typeof navigator === 'undefined' || !navigator.gpu) {
        return { available: false, reason: 'no-api' };
    }

    try {
        let adapter;
        try {
            adapter = await navigator.gpu.requestAdapter({ powerPreference: 'high-performance' });
            if (!adapter) {
                adapter = await navigator.gpu.requestAdapter();
            }
        } catch (_) {
            return { available: false, reason: 'no-adapter' };
        }

        if (!adapter) {
            return { available: false, reason: 'no-adapter' };
        }

        const adapterInfo = emptyAdapterInfo();
        try {
            // Prefer synchronous adapter.info (Chromium often fills this; requestAdapterInfo() can stay redacted).
            if (adapter.info) {
                mergeAdapterInfoNonEmpty(adapterInfo, adapter.info);
            }
            if (typeof adapter.requestAdapterInfo === 'function') {
                const asyncInfo = await adapter.requestAdapterInfo();
                mergeAdapterInfoNonEmpty(adapterInfo, asyncInfo);
            }
        } catch (_) {}

        let limits = null;
        let deviceFailed = false;
        try {
            const device = await adapter.requestDevice();
            try {
                limits = serializeLimits(device.limits);
            } finally {
                if (device && typeof device.destroy === 'function') {
                    device.destroy();
                }
            }
        } catch (_) {
            deviceFailed = true;
        }

        const out = { available: true, adapterInfo, limits };
        if (deviceFailed) {
            out.reason = 'device-failed';
        }
        return out;
    } catch (_) {
        return { available: false, reason: 'error' };
    }
}
